'use client';

import { useCallback } from 'react';
import { useWizardContext } from '../../hooks/useWizardContext';
import { useWizardNavigation } from '../../hooks/useWizardNavigation';
import { WizardMobileBackButton } from '../WizardMobileBackButton';
import { StepLayout } from '../shared/StepLayout';

/**
 * Wrapper component that adds a background image with overlay.
 */
function BackgroundWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div
      className='relative flex min-h-screen w-full flex-col bg-cover bg-center bg-no-repeat'
      style={{ backgroundImage: 'url(/location-check-bg.png)' }}
    >
      {/* White overlay at 45% + black overlay at 60% to darken the background */}
      <div className='absolute inset-0 bg-white/45' />
      <div className='absolute inset-0 bg-black/60' />
      {/* Back button pinned to top */}
      <div className='relative z-10'>
        <WizardMobileBackButton dark />
      </div>
      {/* Content above the overlay, centered in remaining space */}
      <div className='relative z-10 flex flex-1 items-start pt-8 md:items-center md:pt-0'>
        <div className='w-full'>{children}</div>
      </div>
    </div>
  );
}

/**
 * Formats a list of strings into a proper English list with Oxford comma.
 *
 * Examples
 *
 *   formatList(["New York"]) // => "New York"
 *   formatList(["New York", "Northern California"]) // => "New York and Northern California"
 *   formatList(["New York", "Northern California", "South Florida"]) // => "New York, Northern California, and South Florida"
 */
function formatList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/**
 * LocationCheck step - displays nearest Sollis location based on ZIP code.
 * Location data is fetched in ZipCodeStep and stored in wizard state.
 */
export function LocationCheckStep() {
  const { state } = useWizardContext();
  const { goToNextStep, goToPreviousStep, hasPreviousStep } =
    useWizardNavigation();

  const location = state.formData?.nearest_location;

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      goToNextStep();
    },
    [goToNextStep]
  );

  // When no location is found, show general Sollis coverage info
  if (!location) {
    return (
      <BackgroundWrapper>
        <StepLayout
          heading='Sollis has centers in New York, Northern California, Southern California, and South Florida.'
          description="With all-center access plus 24/7 virtual care, we're at the ready whenever you need us."
          onSubmit={handleSubmit}
          onBack={goToPreviousStep}
          showBack={hasPreviousStep}
          darkBackground
        />
      </BackgroundWrapper>
    );
  }

  const allLocations = [
    location.locationName,
    ...location.alternativeLocations,
  ];
  const formattedList = formatList(allLocations);
  const hasPediatrics = state.formData?.location_has_pediatrics;

  return (
    <BackgroundWrapper>
      <StepLayout
        heading={`Sollis has centers in ${formattedList}.`}
        description='You also have unlimited access to all centers and 24/7 virtual care.'
        onSubmit={handleSubmit}
        onBack={goToPreviousStep}
        showBack={hasPreviousStep}
        darkBackground
      >
        {hasPediatrics && (
          <p className='text-center font-sans text-[18px] leading-[130%] font-normal text-white xl:text-[21px]'>
            Have kids? We offer all-in-one primary, urgent, and emergency
            pediatrics with an on-site Concierge Pediatrician.
          </p>
        )}
      </StepLayout>
    </BackgroundWrapper>
  );
}
